import { getSiteConfig } from '@shared/config';
import { NumberFormats as nf, ROUTES, useCurrencyFormatter } from '@shared/utils';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import type { ComponentProps } from 'react';
import { LotteryPurchasePod as Pod } from '../../lotteries/LotteryPurchasePod/LotteryPurchasePod';
import type { LotteryName, RenewType, TicketPurchased } from '../types';
import { getPodBottomStatus, getRenewalPodLabel } from '../utils/getTicketStatuses/getTicketStatuses';

type PurchasePodProps = {
  lotteryName: LotteryName;
  ticketId: string;
  winnings: number;
  numberOfLines: number;
  renewType?: RenewType;
  cancelReceiptAt?: string;
  ticketState: TicketPurchased['ticketState'];
  onClick?: ComponentProps<typeof Link>['onClick'];
} & Omit<ComponentProps<typeof Pod>, 'children' | 'onClick'>;

const { PURCHASES } = ROUTES.LOTTO;

/**
 * Purchase Pod implements a component for displaying lottery purchase details. It uses LotteryPurchasePod as a base.
 * Navigation to ticket details with NextLink.
 * @param lotteryName - Name of the lottery.
 * @param ticketId - The ID of the ticket, to be used in building link href.
 * @param winnings - The winnings amount.
 * @param numberOfLines - The number of lines purchased.
 * @param renewType - The type of renewal, if any.
 * @param cancelReceiptAt - The date the ticket was cancelled.
 * @param ticketState - The state of the ticket.
 * @param onClick - Optional click handler for the pod link.
 * @returns NextLink with LotteryPurchasePod UI
 */
function PurchasePod({ lotteryName, ticketId, numberOfLines, winnings, renewType, cancelReceiptAt, ticketState, onClick, ...props }: PurchasePodProps) {
  const t = useTranslations('lotteries.purchase');
  const cf = useCurrencyFormatter();
  const { jackpotWinThreshold } = getSiteConfig().LOTTERY_PURCHASES;

  const bottomStatus = getPodBottomStatus({ winnings, renewType, cancelReceiptAt, ticketState, jackpotWinThreshold });
  const formattedWinnings = bottomStatus?.showAmount ? cf(winnings).format(nf.WITH_DECIMALS).get() : null;
  const renewalLabel = getRenewalPodLabel({ renewType, cancelReceiptAt, ticketState });

  return (
    <Link href={`${PURCHASES}/${ticketId}`} data-testid={`purchase-pod-${ticketId}`} onClick={onClick}>
      <Pod {...props}>
        {renewalLabel ? (
          <Pod.Label variant={renewalLabel.variant} data-testid="purchase-pod-renewal-label">
            {t(renewalLabel.translationKey)}
          </Pod.Label>
        ) : null}
        <Pod.Content lotteryName={lotteryName}>
          <Pod.Detail>{t('lines', { number: numberOfLines })}</Pod.Detail>
        </Pod.Content>
        {bottomStatus ? (
          <Pod.Status status={bottomStatus.variant}>
            <span>{t(bottomStatus.translationKey)}</span>
            {formattedWinnings ? <span className="paragraph-s-strong ml-auto text-success-foreground">{formattedWinnings}</span> : null}
          </Pod.Status>
        ) : null}
      </Pod>
    </Link>
  );
}

export { PurchasePod };
